import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { Database, getDatabase, onValue, ref, update } from 'firebase/database';

import { AgricolaAssistanceFirebaseStatus } from '../interfaces/agricola.interface';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AgricolaFirebaseSyncService {
  private database: Database | null = null;

  isConfigured(): boolean {
    const config = environment.firebase || {};
    return [
      config.apiKey,
      config.authDomain,
      config.databaseURL,
      config.projectId,
      config.appId
    ].every(value => typeof value === 'string' && value.trim().length > 0);
  }

  watchAssistanceCompletion(emissionLogId: number | string, origin = 'externo'): Observable<AgricolaAssistanceFirebaseStatus> {
    return new Observable<AgricolaAssistanceFirebaseStatus>(subscriber => {
      const database = this.ensureDatabase();
      if (!database) {
        subscriber.error(new Error('Firebase no está configurado para escuchar la finalización de la asistencia.'));
        return undefined;
      }

      const entryRef = ref(database, `s2_assistances/${origin}/${emissionLogId}`);
      const unsubscribe = onValue(entryRef, snapshot => {
        const value = snapshot.val();
        if (!value || typeof value !== 'object') {
          return;
        }

        const raw = value as Record<string, unknown>;
        const rawAttachmentId = raw['attachment_id'] ?? raw['attachmentId'];
        subscriber.next({
          status: Number(raw['status']) || 0,
          stage: this.firstString(raw, ['stage']),
          policyNumber: this.firstString(raw, ['policy_number', 'policy', 'transactionId', 'transaction_id']),
          documentUrl: this.firstString(raw, ['download_url', 'document_url', 'url']),
          attachmentId: rawAttachmentId != null && Number.isFinite(Number(rawAttachmentId)) ? Number(rawAttachmentId) : null,
          transactionId: this.firstString(raw, ['transactionId', 'transaction_id', 'policy_number', 'policy']),
          signedAt: this.firstString(raw, ['signed_at', 'signedAt']),
          signerName: this.firstString(raw, ['signer_name', 'signerName']),
          rejected: raw['rejected'] === true,
          rejectedReason: this.firstString(raw, ['rejected_reason', 'rejectedReason']),
          raw
        });
      }, error => subscriber.error(error));

      return () => unsubscribe();
    });
  }

  // Writes the "emitted" phase after the seller validates the signature and emits.
  // This is the seller side of the two-phase channel (Decision B: Firebase is
  // written client-side): flipping status to 1 is what releases the customer's
  // waiting page into its purchase summary. Non-fatal — the source of truth is
  // EmissionLog.status server-side.
  async writeAssistanceEmissionResult(
    emissionLogId: number | string,
    result: { policyNumber: string; documentUrl: string; transactionId: string },
    origin = 'externo'
  ): Promise<void> {
    const database = this.ensureDatabase();
    if (!database) {
      throw new Error('Firebase no está configurado para publicar la emisión de la asistencia.');
    }

    const entryRef = ref(database, `s2_assistances/${origin}/${emissionLogId}`);
    await update(entryRef, {
      stage: 'emitted',
      status: 1,
      policy_number: result.policyNumber,
      download_url: result.documentUrl,
      payment_status: 'completed',
      payment_completed_at: new Date().toISOString(),
      transaction_id: result.transactionId,
      transaction_number: result.transactionId
    });
  }

  // Writes the "signed" phase from the customer public page (validate_signature_by_seller).
  // This is the customer side of the two-phase channel: it lets the seller's waiting
  // screen (Part A) surface the signature for review. Non-fatal — the source of truth
  // is EmissionLog.status=2 server-side; a reload reconstructs the state.
  async writeAssistanceSignature(
    emissionLogId: number | string,
    signature: { attachmentId: number | null; signedAt: string; signerName: string },
    origin = 'externo'
  ): Promise<void> {
    const database = this.ensureDatabase();
    if (!database) {
      throw new Error('Firebase no está configurado para publicar la firma de la asistencia.');
    }

    const entryRef = ref(database, `s2_assistances/${origin}/${emissionLogId}`);
    await update(entryRef, {
      stage: 'signed',
      status: 2,
      attachment_id: signature.attachmentId,
      signed_at: signature.signedAt,
      signer_name: signature.signerName,
      // Clear any prior rejection so a re-sign presents a clean 'signed' state.
      rejected: false,
      rejected_reason: null
    });
  }

  // Writes the "rejected" transition from the seller: resets the node to pending so
  // the customer's waiting page returns to the signature step. Pairs with the S2
  // reject endpoint that moves EmissionLog.status back to 0 (the source of truth).
  async writeAssistanceRejection(
    emissionLogId: number | string,
    reason: string,
    origin = 'externo'
  ): Promise<void> {
    const database = this.ensureDatabase();
    if (!database) {
      throw new Error('Firebase no está configurado para publicar el rechazo de la firma.');
    }

    const entryRef = ref(database, `s2_assistances/${origin}/${emissionLogId}`);
    await update(entryRef, {
      stage: 'pending',
      status: 0,
      attachment_id: null,
      rejected: true,
      rejected_reason: reason,
      rejected_at: new Date().toISOString()
    });
  }

  private ensureDatabase(): Database | null {
    if (!this.isConfigured()) {
      return null;
    }

    if (this.database) {
      return this.database;
    }

    const appName = 'agricola-private-link';
    const app = getApps().some(item => item.name === appName)
      ? getApp(appName)
      : initializeApp(environment.firebase, appName);
    this.database = getDatabase(app);
    return this.database;
  }

  private firstString(record: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = record[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }

    return '';
  }
}
